import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../../config/api.config';
import { 
  Flex, 
  Text, 
  Button, 
  Badge,
  Card,
  Box,
  IconButton,
  Separator
} from '@radix-ui/themes';
import { Cross2Icon, Pencil1Icon, UploadIcon, CheckIcon, Cross1Icon, ArrowUpIcon, CopyIcon } from '@radix-ui/react-icons';

interface SettingsPopupProps {
  show: boolean;
  onHide: () => void;
  userName?: string;
  userRole?: string;
  schoolName?: string;
  schoolLogo?: string;
}

interface PlanInfo {
  name: string;
  formattedName: string;
  totalStudents: number;
  studentLimit: number;
}

interface TrialInfo {
  expiresAt: string;
  teacherCount: number;
  teacherLimit: number;
  studentCount: number;
  studentLimit: number;
}

interface PlanLimits {
  classes: { current: number; limit: number; isLoading?: boolean };
  questions: { current: number; limit: number; isLoading?: boolean };
  questionsPerClass: { current: number; limit: number; isLoading?: boolean };
  students: { current: number; limit: number; isLoading?: boolean };
}

interface UpdateSuccess {
  logo: boolean;
  name: boolean;
}

const SettingsPopup: React.FC<SettingsPopupProps> = ({ 
  show, 
  onHide, 
  userName, 
  userRole, 
  schoolName: propSchoolName, 
  schoolLogo 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [schoolCode, setSchoolCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTrialAccount, setIsTrialAccount] = useState<boolean>(false);
  const [planInfo, setPlanInfo] = useState<PlanInfo>({
    name: '',
    formattedName: '',
    totalStudents: 0,
    studentLimit: 0
  });
  const [trialInfo, setTrialInfo] = useState<TrialInfo>({
    expiresAt: '',
    teacherCount: 0,
    teacherLimit: 0,
    studentCount: 0,
    studentLimit: 0
  });
  const [copied, setCopied] = useState<boolean>(false);
  const [editingSchoolName, setEditingSchoolName] = useState<boolean>(false);
  const [newSchoolName, setNewSchoolName] = useState<string>('');
  const [schoolNameError, setSchoolNameError] = useState<string>('');
  const [newLogoFile, setNewLogoFile] = useState<File | null>(null);
  const [newLogoPreview, setNewLogoPreview] = useState<string>('');
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [logoUploadError, setLogoUploadError] = useState<string>('');
  const [isUpdatingSchoolName, setIsUpdatingSchoolName] = useState<boolean>(false);
  const [updateSuccess, setUpdateSuccess] = useState<UpdateSuccess>({ logo: false, name: false });
  const [actualSchoolName, setActualSchoolName] = useState<string>(propSchoolName || '');
  const [planLimits, setPlanLimits] = useState<PlanLimits>({
    classes: { current: 0, limit: 0, isLoading: true },
    questions: { current: 0, limit: 0, isLoading: true },
    questionsPerClass: { current: 0, limit: 0, isLoading: true },
    students: { current: 0, limit: 0, isLoading: true }
  });
  const [exceedsLimits, setExceedsLimits] = useState<boolean>(false);
  const [shouldUpgrade, setShouldUpgrade] = useState<boolean>(false);
  const [upgradeError, setUpgradeError] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshError, setRefreshError] = useState<string>('');
  const refreshInterval = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (show && (userRole?.toLowerCase() === 'admin' || userRole?.toLowerCase() === 'teacher')) {
      fetchSchoolDetails();
    }
  }, [show, userRole]);

  useEffect(() => {
    if (show && actualSchoolName) {
      setNewSchoolName(actualSchoolName);
    }
  }, [show, actualSchoolName]);

  useEffect(() => {
    if (!show) {
      setEditingSchoolName(false);
      setNewLogoFile(null);
      setNewLogoPreview('');
      setLogoUploadError('');
      setSchoolNameError('');
      setUpdateSuccess({ logo: false, name: false });
    }
  }, [show]);

  useEffect(() => {
    if (show && userRole?.toLowerCase() === 'teacher' && !isTrialAccount) {
      fetchUsageData();

      refreshInterval.current = setInterval(() => {
        fetchUsageData(true);
      }, 30000);

      return () => {
        if (refreshInterval.current) {
          clearInterval(refreshInterval.current);
        }
      };
    }
  }, [show, userRole, isTrialAccount]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  const fetchSchoolDetails = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        getApiUrl('/api/v1/stem@home/auth/school-details'),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        if (data.schoolCode) {
          setSchoolCode(data.schoolCode);
        }
        
        if (data.schoolName) {
          setActualSchoolName(data.schoolName);
          setNewSchoolName(data.schoolName);
        }
        
        setIsTrialAccount(data.isTrial === true);
        
        const planName = data.plan || '';
        let formattedPlanName = '';
        
        if (planName.toLowerCase().includes('personal')) {
          formattedPlanName = 'Personal Learning Plan';
        } else if (planName.toLowerCase().includes('classroom')) {
          formattedPlanName = 'Classroom Success Plan';
        } else if (planName.toLowerCase().includes('institution')) {
          formattedPlanName = 'Institution Growth Plan';
        } else {
          formattedPlanName = planName || 'Personal Learning Plan';
        }
        
        setPlanInfo({
          name: planName,
          formattedName: formattedPlanName,
          totalStudents: data.totalStudents || 0,
          studentLimit: getPlanLimits(planName.toLowerCase()).studentLimit
        });

        if (userRole?.toLowerCase() === 'teacher' && !data.isTrial) {
          fetchUsageData();
        }
        
        if (data.isTrial && data.trialDetails) {
          setTrialInfo({
            expiresAt: data.trialDetails?.expiresAt || '',
            teacherCount: data.trialDetails?.usage?.teacherCount || 0,
            teacherLimit: data.trialDetails?.limits?.maxTeachers || 0,
            studentCount: data.trialDetails?.usage?.studentCount || 0,
            studentLimit: data.trialDetails?.limits?.maxStudents || 0,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching school details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsageData = async (isRefresh: boolean = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      }
      setRefreshError('');

      setPlanLimits(prev => ({
        ...prev,
        classes: { ...prev.classes, isLoading: true },
        questions: { ...prev.questions, isLoading: true },
        students: { ...prev.students, isLoading: true },
        questionsPerClass: { ...prev.questionsPerClass, isLoading: true }
      }));

      // Fetch school details only once to get all usage stats
      const schoolResponse = await axios.get(
        getApiUrl('/api/v1/stem@home/auth/school-details'),
        { headers: getAuthHeaders() }
      );

      if (!schoolResponse.data?.success) {
        throw new Error('Failed to fetch school details');
      }

      const usage = schoolResponse.data.data?.usage || {};
      const planType = schoolResponse.data.data?.planType?.toLowerCase() || 'personal';

      setPlanLimits({
        classes: { current: usage.classes?.current || 0, limit: usage.classes?.limit || 0, isLoading: false },
        questions: { current: usage.questions?.current || 0, limit: usage.questions?.limit || 0, isLoading: false },
        questionsPerClass: { current: usage.questions?.currentPerClass || 0, limit: usage.questions?.perClassLimit || 0, isLoading: false },
        students: { current: usage.students?.current || 0, limit: usage.students?.limit || 0, isLoading: false }
      });

      setLastRefresh(new Date());

      const exceedsAnyLimit = (
        (usage.classes?.limit && usage.classes.current > usage.classes.limit) ||
        (usage.questions?.limit && usage.questions.current > usage.questions.limit) ||
        (usage.questions?.perClassLimit && usage.questions.currentPerClass > usage.questions.perClassLimit) ||
        (usage.students?.limit && usage.students.current > usage.students.limit)
      );

      setExceedsLimits(exceedsAnyLimit);
      setShouldUpgrade(exceedsAnyLimit && planType === 'personal');

    } catch (error) {
      console.error('Error fetching usage data:', error);
      setRefreshError('Failed to update usage data');
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      }
    }
  };

  const getPlanLimits = (planType: string) => {
    switch (planType) {
      case 'classroom':
        return {
          classLimit: 30,
          questionLimit: 500,
          questionPerClassLimit: 50,
          studentLimit: 30
        };
      case 'institution':
        return {
          classLimit: 100,
          questionLimit: 2000,
          questionPerClassLimit: 100,
          studentLimit: 100
        };
      case 'trial':
        return {
          classLimit: 2,
          questionLimit: 3,
          questionPerClassLimit: 3,
          studentLimit: 3
        };
      default: // personal plan
        return {
          classLimit: 5,
          questionLimit: 50,
          questionPerClassLimit: 10,
          studentLimit: 1
        };
    }
  };

  const formatSchoolCode = (code: string): string => {
    if (!code || typeof code !== 'string') return code;
    
    const formatted: string[] = [];
    for (let i = 0; i < code.length; i += 4) {
      formatted.push(code.substring(i, i + 4));
    }
    return formatted.join(' ');
  };

  const copySchoolCode = () => {
    if (!schoolCode) return;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(schoolCode)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          fallbackCopyTextToClipboard(schoolCode);
        });
    } else {
      fallbackCopyTextToClipboard(schoolCode);
    }
  };
  
  const fallbackCopyTextToClipboard = (text: string) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Fallback clipboard copy error:', err);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Unknown';
    }
  };

  const getDaysRemaining = (expiryDate: string): number => {
    if (!expiryDate) return 0;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      const diffTime = expiry.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch (e) {
      return 0;
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoUploadError('');
    setUpdateSuccess({ ...updateSuccess, logo: false });
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setLogoUploadError('Please select a valid image file (JPG, PNG, GIF, SVG)');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      setLogoUploadError('Image size should be less than 2MB');
      return;
    }
    
    setNewLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUploadClick = () => {
    fileInputRef.current?.click();
  };

  const uploadLogo = async () => {
    if (!newLogoFile) return;
    
    setIsUploadingLogo(true);
    setLogoUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('logo', newLogoFile);
      
      const response = await axios.post(
        getApiUrl('/api/v1/stem@home/school/update-logo'),
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setUpdateSuccess({ ...updateSuccess, logo: true });
        setTimeout(() => {
          setUpdateSuccess({ ...updateSuccess, logo: false });
        }, 3000);
      } else {
        setLogoUploadError(response.data.message || 'Failed to upload logo');
      }
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setLogoUploadError(err.response?.data?.message || 'Network error while uploading logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const startEditingSchoolName = () => {
    setEditingSchoolName(true);
    setNewSchoolName(actualSchoolName || '');
    setSchoolNameError('');
  };

  const cancelEditingSchoolName = () => {
    setEditingSchoolName(false);
    setNewSchoolName(actualSchoolName || '');
    setSchoolNameError('');
  };

  const handleSchoolNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewSchoolName(e.target.value);
    setSchoolNameError('');
  };

  const saveSchoolName = async () => {
    if (!newSchoolName.trim()) {
      setSchoolNameError('School name cannot be empty');
      return;
    }
    
    setIsUpdatingSchoolName(true);
    setSchoolNameError('');
    
    try {
      const response = await axios.post(
        getApiUrl('/api/v1/stem@home/school/update-name'),
        { name: newSchoolName.trim() },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setEditingSchoolName(false);
        setActualSchoolName(newSchoolName.trim());
        setUpdateSuccess({ ...updateSuccess, name: true });
        setTimeout(() => {
          setUpdateSuccess({ ...updateSuccess, name: false });
        }, 3000);
      } else {
        setSchoolNameError(response.data.message || 'Failed to update school name');
      }
    } catch (err: any) {
      console.error('Error updating school name:', err);
      setSchoolNameError(err.response?.data?.message || 'Network error while updating school name');
    } finally {
      setIsUpdatingSchoolName(false);
    }
  };

  const handleSave = async () => {
    if (canEditSchoolSettings && userRole?.toLowerCase() !== 'student' && newSchoolName !== actualSchoolName) {
      await saveSchoolName();
    }
    onHide();
  };

  const canEditSchoolSettings = !isTrialAccount && (
    ((planInfo.formattedName === 'Personal Learning Plan' || planInfo.formattedName === 'Classroom Success Plan') && userRole?.toLowerCase() === 'teacher') ||
    (planInfo.formattedName === 'Institution Growth Plan' && userRole?.toLowerCase() === 'admin')
  );
  const canUpgrade = !isTrialAccount && 
    planInfo.formattedName !== 'Institution Growth Plan' && 
    userRole?.toLowerCase() === 'teacher';

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  const handleUpgradeClick = async () => {
    setUpgradeError('');
    try {
      const response = await axios.post(
        getApiUrl('/api/v1/stem@home/payment/create-portal-session'),
        { flowType: 'subscription_update' },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setUpgradeError('Failed to create upgrade session. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      setUpgradeError('Failed to create upgrade session. Please try again or contact support.');
    }
  };

  const handleHideUpgrade = () => {
    setUpgradeError('');
  };

  const getRoleBadgeColor = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'student': return 'orange';
      case 'teacher': return 'blue';
      case 'admin': return 'green';
      default: return 'gray';
    }
  };

  const getRoleDisplayName = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'student': return 'Student';
      case 'teacher': return 'Teacher';
      case 'admin': return 'Admin';
      default: return role || 'Unknown';
    }
  };

  if (!show) return null;

  console.log('SettingsPopup render called with show:', show);

  const renderPlanUsage = () => {
    if (!isTrialAccount && userRole?.toLowerCase() === 'teacher') {
      return (
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            color: '#6b7280', 
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px' 
          }}>
            PLAN USAGE
            {isRefreshing && <span style={{ marginLeft: '8px', color: '#9CA3AF' }}>(Refreshing...)</span>}
            {!isRefreshing && lastRefresh && (
              <span style={{ marginLeft: '8px', color: '#9CA3AF', fontSize: '10px' }}>
                (Last updated: {lastRefresh.toLocaleTimeString()})
              </span>
            )}
          </label>
          
          {refreshError && (
            <div style={{ 
              padding: '8px', 
              marginBottom: '12px',
              backgroundColor: '#FEE2E2', 
              border: '1px solid #FCA5A5',
              borderRadius: '4px',
              color: '#DC2626',
              fontSize: '12px'
            }}>
              {refreshError}
            </div>
          )}

          <div style={{ 
            padding: '16px', 
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(planLimits)
                .filter(([key]) => key !== 'questionsPerClass')
                .map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    </span>
                    <span style={{ 
                      fontSize: '14px', 
                      color: value.isLoading ? '#9CA3AF' : (value.current >= value.limit ? '#DC2626' : '#6B7280')
                    }}>
                      {value.isLoading ? 'Loading...' : `${value.current}/${value.limit === Infinity ? '∞' : value.limit}`}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const modalContent = (
    <div
      className="settings-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="settings-modal"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '24px 24px 0 24px' 
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#111827' 
          }}>
            Settings
          </h2>
          <button
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280'
            }}
          >
            <Cross2Icon width={18} height={18} />
          </button>
        </div>

        {/* Content */}
        <div className="popup-content" style={{ 
          flex: 1,
          overflow: 'hidden',
          padding: '24px', 
          maxHeight: 'calc(90vh - 140px)', 
          overflowY: 'auto',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {planInfo.formattedName === 'Institution Growth Plan' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <div style={{ 
                  width: '128px',
                  height: '96px',
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {newLogoPreview ? (
                    <img src={newLogoPreview} alt="New School Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }} />
                  ) : schoolLogo ? (
                    <img src={schoolLogo} alt="School Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }} />
                  ) : (
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>SCHOOL LOGO</span>
                  )}
                </div>
                
                {canEditSchoolSettings && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input 
                      type="file" 
                      ref={fileInputRef} 
                      style={{ display: 'none' }} 
                      accept="image/*" 
                      onChange={handleLogoChange} 
                    />
                    <IconButton 
                      variant="outline" 
                      size="2"
                      onClick={handleLogoUploadClick}
                      title="Upload new logo"
                    >
                      <UploadIcon />
                    </IconButton>
                    
                    {newLogoFile && (
                      <IconButton 
                        variant="solid" 
                        size="2"
                        onClick={uploadLogo}
                        disabled={isUploadingLogo}
                        title="Save new logo"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                  </div>
                )}
                
                {logoUploadError && (
                  <span style={{ fontSize: '12px', color: '#ef4444' }}>{logoUploadError}</span>
                )}
                
                {updateSuccess.logo && (
                  <span style={{ fontSize: '12px', color: '#10b981' }}>Logo updated successfully!</span>
                )}
              </div>
            )}
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#6b7280', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px' 
              }}>NAME</label>
              <input
                value={userName || 'A'}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#f9fafb',
                  fontSize: '14px',
                  textAlign: 'center',
                  color: '#111827',
                  fontWeight: '400',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#6b7280', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px' 
              }}>ROLE</label>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '6px 16px',
                  backgroundColor: getRoleBadgeColor(userRole) === 'blue' ? '#3b82f6' : 
                                  getRoleBadgeColor(userRole) === 'green' ? '#10b981' : 
                                  getRoleBadgeColor(userRole) === 'orange' ? '#f59e0b' : '#6b7280',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '999px',
                  textTransform: 'capitalize'
                }}>
                  {getRoleDisplayName(userRole)}
                </span>
              </div>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#6b7280', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px' 
              }}>SCHOOL</label>
              {canEditSchoolSettings && userRole?.toLowerCase() !== 'student' ? (
                <input
                  value={newSchoolName}
                  onChange={handleSchoolNameChange}
                  placeholder="Enter school name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#111827',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              ) : (
                <input
                  value={actualSchoolName || "A's Learning Plan"}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#111827',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              )}
              {schoolNameError && (
                <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{schoolNameError}</div>
              )}
              {updateSuccess.name && (
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>School name updated successfully!</div>
              )}
            </div>

            {['admin', 'teacher'].includes(userRole?.toLowerCase() || '') && (
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#6b7280', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px' 
                }}>SCHOOL CODE</label>
                {isLoading ? (
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading school code...</div>
                ) : schoolCode ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      value={formatSchoolCode(schoolCode)}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#f9fafb',
                        fontSize: '14px',
                        fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                        textAlign: 'center',
                        color: '#111827',
                        letterSpacing: '1px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={copySchoolCode}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        fontSize: '12px',
                        color: '#374151',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        outline: 'none'
                      }}
                      onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#f9fafb'}
                      onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#ffffff'}
                    >
                      {copied ? <CheckIcon /> : <CopyIcon />}
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: '#ef4444' }}>Couldn't load school code</div>
                )}
              </div>
            )}

            {userRole?.toLowerCase() !== 'student' && (
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#6b7280', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px' 
                }}>ACCOUNT TYPE</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 20px',
                    backgroundColor: isTrialAccount ? '#f59e0b' : '#10b981',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '500',
                    borderRadius: '999px'
                  }}>
                    {planInfo.formattedName || 'Personal Learning Plan'}
                  </span>
                  
                  {canUpgrade && (
                    <button
                      onClick={handleUpgradeClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        outline: 'none'
                      }}
                      onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#3b82f6'}
                    >
                      <ArrowUpIcon />
                      Upgrade Plan
                    </button>
                  )}

                  {upgradeError && (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fef2f2', 
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      textAlign: 'center',
                      maxWidth: '100%'
                    }}>
                      <span style={{ fontSize: '12px', color: '#dc2626' }}>
                        {upgradeError}
                      </span>
                    </div>
                  )}

                  {shouldUpgrade && (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fef3c7', 
                      border: '1px solid #fbbf24',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '12px', color: '#92400e' }}>
                        ⚠️ You have exceeded your plan limits. Please upgrade to continue.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {renderPlanUsage()}

            {isTrialAccount && userRole?.toLowerCase() !== 'student' && (
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#6b7280', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px' 
                }}>TRIAL STATUS</label>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>Expires</span>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>{formatDate(trialInfo.expiresAt)}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>Days Remaining</span>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>{getDaysRemaining(trialInfo.expiresAt)}</span>
                    </div>
                    
                    {userRole?.toLowerCase() !== 'student' && (
                      <>
                        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>Teachers</span>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>{trialInfo.teacherCount}/{trialInfo.teacherLimit}</span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>Students</span>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>{trialInfo.studentCount}/{trialInfo.studentLimit}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '24px', 
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px' 
        }}>
          <button
            onClick={onHide}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onHide}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SettingsPopup; 